import { useEffect, useState } from 'react';
import { Select } from 'antd';
import { Icon } from 'Components';
import Alert from '../components/Alert';
import StepFooter from './StepFooter';
import { MAX_RECIPIENTS } from "./../PhishingManager.constants";
import { getInvalidEmails, normalizeEmail } from 'app-util';

type TestConfigurationProps = {
  onBack: () => void;
  onNext: (emails: string[]) => void;
  stepErrorMessage?: string | null;
  setStepErrorMessage: (message: string | null) => void;
};

const CloseIcon = () => (
  <Icon type="campaignStepClose" />
);

const tagRender = (props: any) => {
  const { label, closable, onClose } = props;

  return (
    <span className="ml-chip">
      <span className="ml-chip-text">{label}</span>

      {closable && (
        <span
          className="ml-chip-close"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </span>
      )}
    </span>
  );
};

const TestConfiguration = ({ onBack, onNext, stepErrorMessage, setStepErrorMessage }: TestConfigurationProps) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  const validateEmails = (emails: string[]): string | null => {
    if (emails.length > MAX_RECIPIENTS) {
      return `You can add a maximum of ${MAX_RECIPIENTS} recipients`;
    }
    const invalidEmails = getInvalidEmails(emails);
    if (invalidEmails.length === 1 && emails.length === 1) {
      return 'Enter a valid email address';
    }

    if (invalidEmails.length === 1) {
      return `Email "${invalidEmails[0]}" is invalid, please enter a valid email address.`;
    }

    if (invalidEmails.length > 1) {
      const last = invalidEmails.pop();
      return `Emails "${invalidEmails.join(', ')} and ${last}" are invalid, please enter valid email addresses.`;
    }
    return null;
  };

  const clearExternalError = () => {
    if (stepErrorMessage) {
      setStepErrorMessage(null);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    clearExternalError();
    if (emailError && !stepErrorMessage) {
      setEmailError(null);
    }
  };

  const handleChange = (values: string[]) => {
    const deduped = [...new Set(values.map(normalizeEmail))];
    setRecipients(deduped);
    clearExternalError();
    setEmailError(validateEmails(deduped));
  };

  useEffect(() => {
    if (stepErrorMessage) {
      setEmailError(stepErrorMessage);
    }
  }, [stepErrorMessage]);

  const handleDeselect = (value: string) => {
    const normalized = normalizeEmail(value);
    const trimmedSearch = normalizeEmail(searchValue);
    let updated: string[];
    if (trimmedSearch === normalized && recipients.includes(normalized)) {
      updated = [...recipients.filter(v => normalizeEmail(v) !== normalized), normalized];
      setSearchValue('');
    } else {
      updated = recipients.filter(v => normalizeEmail(v) !== normalized);
    }
    setRecipients(updated);
    clearExternalError();
    const error = validateEmails(updated);
    setEmailError(error);
  };

  const isNextDisabled =
    recipients.length === 0 ||
    !!emailError ||
    recipients.length > MAX_RECIPIENTS;

  return (
    <>
      <div className="test-configuration-container step-card">
        <div className="card-header">
          <div className="card-title">Test Configuration</div>
          <div className="card-subtitle">
            Select users to receive a test phishing email to verify setup before the full campaign
          </div>
        </div>

        <div className="test-config-form">
          <label className="form-label">
            Test recipients <span className="required-asterisk">*</span>
          </label>

          <div className="email-input-wrapper">
            <Select
              mode="tags"
              size="large"
              value={recipients}
              onSearch={handleSearch}
              onChange={handleChange}
              onDeselect={handleDeselect}
              placeholder="Enter one or more email addresses"
              tokenSeparators={[',', ' ']}
              className="ml-input-chip"
              dropdownClassName="campaign-setup-filter-option-select"
              style={{ width: '100%' }}
              notFoundContent={null}
              getPopupContainer={(trigger) => trigger.parentElement}
              open={false}
              tagRender={tagRender}
            />
            {emailError && (
              <div className="ml-input-help-text">
                {emailError}
              </div>
            )}
          </div>
        </div>

        <div className="alert-section">
          <Alert title="Information" subtitle="Test emails are sent within a few minutes after the test campaign is started." />
        </div>
      </div>

      <StepFooter
        onBack={onBack}
        onNext={() => onNext(recipients)}
        nextBtnDisabled={isNextDisabled}
      />
    </>
  );
};

export default TestConfiguration;